/* eslint-disable tsdoc/syntax -- have conflicting examples */
import { z } from 'zod';

// Used for non-cosmetic critical fields that must be present,
// otherwise the form's functionality is broken for the user.
export const CRITICAL_FIELD_ERROR = 'CRITICAL_FIELD_ERROR';

// Conditions

const ConditionBase = z.object({
  operator: z.string(CRITICAL_FIELD_ERROR),
  type: z.enum(['visible'], CRITICAL_FIELD_ERROR),
  field: z.string(CRITICAL_FIELD_ERROR),
});

const ConditionExistsZod = ConditionBase.extend({
  operator: z.literal('exists', CRITICAL_FIELD_ERROR),
});

export type ConditionExists = z.infer<typeof ConditionExistsZod>;

const ConditionEqualsZod = ConditionBase.extend({
  operator: z.literal('equals', CRITICAL_FIELD_ERROR),
  value: z.unknown(),
});

export type ConditionEquals = z.infer<typeof ConditionEqualsZod>;

const ConditionNotEqualsZod = ConditionBase.extend({
  operator: z.literal('not_equals', CRITICAL_FIELD_ERROR),
  value: z.unknown(),
});

export type ConditionNotEquals = z.infer<typeof ConditionNotEqualsZod>;

const ConditionInZod = ConditionBase.extend({
  operator: z.literal('in', CRITICAL_FIELD_ERROR),
  value: z.array(z.unknown(), CRITICAL_FIELD_ERROR),
});

export type ConditionIn = z.infer<typeof ConditionInZod>;

const ConditionContainsZod = ConditionBase.extend({
  operator: z.literal('contains', CRITICAL_FIELD_ERROR),
  value: z.unknown(),
});

export type ConditionContains = z.infer<typeof ConditionContainsZod>;

const ConditionSchema = z.discriminatedUnion('operator', [
  ConditionExistsZod,
  ConditionEqualsZod,
  ConditionNotEqualsZod,
  ConditionInZod,
  ConditionContainsZod,
]);

export const ConditionsArray = ConditionSchema.array().optional();

export type Condition = z.infer<typeof ConditionSchema>;

// Validations

export type ValidationRule<T> = T | { value: T; message: string };
export function validationRuleSchema<T extends z.ZodTypeAny>(base: T) {
  return z.union([base, z.object({ value: base, message: z.string() })]);
}

// Option

export const OptionSchema = z.object({
  value: z.string(CRITICAL_FIELD_ERROR),
  label: z.string(CRITICAL_FIELD_ERROR),
});

// Disclaimer

export const DisclaimerSchema = z.object({
  conditions: ConditionsArray,
  level: z.enum(['info', 'warning', 'error', 'promo']),
  message: z.string(),
});
export type Disclaimer = z.infer<typeof DisclaimerSchema>;

// Fields

export const FieldBase = <T extends string>(type: T) =>
  z.object({
    type: z.literal(type, CRITICAL_FIELD_ERROR),
    name: z.string(CRITICAL_FIELD_ERROR),
    conditions: ConditionsArray,
    subtitle: z.string().optional().nullable(),
    disclaimer: DisclaimerSchema.optional(),
    logical_operator: z.enum(['AND', 'OR']).optional().nullable(),
  });

// z.infer<typeof FieldBase> loses the generic T because FieldBase
// is a function, not a concrete zod schema. We reconstruct the generic
// by inferring the return type of this function and overriding
// the `type` field to a generic again.
export type FieldBase<T extends string = string> = Omit<
  z.infer<ReturnType<typeof FieldBase>>,
  'type'
> & { type: T };

export const FieldTextSchema = FieldBase('text')
  .extend({
    label: z.string().optional().nullable(),
    'aria-label': z.string().optional(),
    placeholder: z.string(),
    description: z.string().optional().nullable(),
    validations: z
      .object({
        optional: validationRuleSchema(z.boolean()).optional(),
        pattern: z.object({ value: z.string(), error: z.string() }).optional(),
      })
      .optional(),
    parameters: z
      .object({
        disabled: z.boolean().optional(),
      })
      .optional(),
  })
  .refine(input => typeof input.label === 'string' || typeof input['aria-label'] === 'string', {
    error: 'Either "label" or "aria-label" must be defined in a "text" field',
  });

export type FieldText = z.infer<typeof FieldTextSchema>;

export const FieldTextareaSchema = FieldBase('textarea').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
    })
    .optional(),
});
export type FieldTextarea = z.infer<typeof FieldTextareaSchema>;

export const FieldNumberSchema = FieldBase('number').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  format: z
    .union([
      z.object({ style: z.literal('currency'), currency: z.literal('EUR') }),
      z.object({ style: z.literal('percent') }),
    ])
    .optional(),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
      step: z.number().optional(),
      min: z.number().optional(),
      max: z.number().optional(),
    })
    .optional(),
});
export type FieldNumber = z.infer<typeof FieldNumberSchema>;

export const FieldSelectSchema = FieldBase('select').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  options: z.array(OptionSchema),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
    })
    .optional(),
});
export type FieldSelect = z.infer<typeof FieldSelectSchema>;

export const FieldMultiSelectSchema = FieldBase('multiSelect').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  options: z.array(OptionSchema),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
    })
    .optional(),
});
export type FieldMultiSelect = z.infer<typeof FieldMultiSelectSchema>;

export const FieldRadioSchema = FieldBase('radio').extend({
  label: z.string(),
  description: z.string().optional().nullable(),
  options: z.array(OptionSchema),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
    })
    .optional(),
});
export type FieldRadio = z.infer<typeof FieldRadioSchema>;

export const FieldFileSchema = FieldBase('file').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  conditions: ConditionsArray,
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
      accept: z.array(z.string()).optional(),
      maxSize: z.number().optional(),
    })
    .optional(),
  parameters: z
    .object({
      multiple: z.boolean().optional(),
    })
    .optional(),
});
export type FieldFile = z.infer<typeof FieldFileSchema>;

export const FieldDisclaimerSchema = FieldBase('disclaimer').extend({
  level: z.enum(['info', 'warning', 'error', 'promo']),
  message: z.string(),
  disclaimer: z.never().optional(), // Disclaimers can't have disclaimers
});
export type FieldDisclaimer = z.infer<typeof FieldDisclaimerSchema>;

export const FieldTitleSchema = FieldBase('title').extend({
  message: z.string(),
  disclaimer: z.never().optional(),
  subtitle: z.never().optional(),
});
export type FieldTitle = z.infer<typeof FieldTitleSchema>;

export const FieldSubtitleSchema = FieldBase('subtitle').extend({
  message: z.string(),
  subtitle: z.never().optional(),
  disclaimer: z.never().optional(),
});
export type FieldSubtitle = z.infer<typeof FieldSubtitleSchema>;

export const FieldSelectCountriesSchema = FieldBase('selectCountries').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
      recommended: z.array(z.string()).optional(),
    })
    .optional(),
});
export type FieldSelectCountries = z.infer<typeof FieldSelectCountriesSchema>;

export const FieldSelectCountrySchema = FieldBase('selectCountry').extend({
  label: z.string(),
  placeholder: z.string(),
  description: z.string().optional().nullable(),
  validations: z
    .object({
      optional: validationRuleSchema(z.boolean()).optional(),
    })
    .optional(),
  parameters: z
    .object({
      disabled: z.boolean().optional(),
      recommended: z.array(z.string()).optional(),
    })
    .optional(),
});
export type FieldSelectCountry = z.infer<typeof FieldSelectCountrySchema>;

export const FieldSchema = z.discriminatedUnion('type', [
  FieldTextSchema,
  FieldTextareaSchema,
  FieldNumberSchema,
  FieldSelectSchema,
  FieldMultiSelectSchema,
  FieldRadioSchema,
  FieldFileSchema,
  FieldDisclaimerSchema,
  FieldTitleSchema,
  FieldSubtitleSchema,
  FieldSelectCountriesSchema,
  FieldSelectCountrySchema,
]);

export type FieldSchema = z.infer<typeof FieldSchema>;

export type Field =
  | FieldText
  | FieldTextarea
  | FieldNumber
  | FieldSelect
  | FieldMultiSelect
  | FieldRadio
  | FieldFile
  | FieldDisclaimer
  | FieldTitle
  | FieldSubtitle
  | FieldSelectCountries
  | FieldSelectCountry;

/**
 * Allows custom field types to be defined.
 *
 * type MySchema = Schema<{ type: 'custom' }>;
 */
export type Schema<F extends { type: string } = never> = (Field | F)[];

// Field type extraction
export type FieldType<T> = T extends { type: infer U } ? U : never;
